import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useAction } from '../components/toast'
import { Badge, Card, Kpi, Loaded, Notice, Page, ReasonDialog, Table } from '../components/ui'
import { get, post } from '../lib/api'
import { count, dateTime, percent, titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type Queue = {
  requests: {
    id: string
    user_id: string
    kind: string
    status: string
    created_at: string | null
    completed_at: string | null
    artifact_file_id: string | null
    days_open: number
    sla_days: number
  }[]
  consents: { purpose: string; granted: number; declined: number; versions: string[] }[]
}

const NEXT_STATUS: Record<string, { label: string; value: string; danger?: boolean }[]> = {
  queued: [
    { label: 'Start work', value: 'in_progress' },
    { label: 'Reject', value: 'rejected', danger: true },
  ],
  in_progress: [
    { label: 'Mark completed', value: 'completed' },
    { label: 'Reject', value: 'rejected', danger: true },
  ],
  completed: [],
  rejected: [],
}

function slaTone(days: number, sla: number): 'green' | 'orange' | 'alert' {
  if (days > sla) return 'alert'
  if (days > sla * 0.66) return 'orange'
  return 'green'
}

export function PrivacyRequests() {
  const run = useAction()
  const queue = useResource((signal) => get<Queue>('/v1/admin/privacy/requests', undefined, signal))
  const [acting, setActing] = useState<{ id: string; status: string; label: string } | null>(null)

  const open = (queue.data?.requests ?? []).filter(
    (r) => r.status === 'queued' || r.status === 'in_progress',
  )
  const breached = open.filter((r) => r.days_open > r.sla_days)

  return (
    <Page
      title="Consents & data rights"
      subtitle="Export and deletion requests, and what people have actually agreed to. A deletion request is a legal clock, not a support ticket."
    >
      {breached.length > 0 && (
        <Notice tone="phi">
          <strong>{breached.length} request{breached.length === 1 ? ' is' : 's are'} past the
          statutory window.</strong> GDPR Article 12 gives one month from receipt. An overrun is a
          reportable failure, not a backlog item.
        </Notice>
      )}

      <div className="grid grid--kpi">
        <Kpi label="Open requests" value={count(open.length)} />
        <Kpi
          label="Past SLA"
          value={count(breached.length)}
          tone={breached.length > 0 ? 'alert' : undefined}
        />
        <Kpi
          label="Deletions queued"
          value={count(open.filter((r) => r.kind === 'delete').length)}
          tone="orange"
        />
        <Kpi
          label="Exports queued"
          value={count(open.filter((r) => r.kind === 'export').length)}
          tone="cyan"
        />
      </div>

      <Card title="Request queue" flush>
        <Loaded resource={queue}>
          {(data) => (
            <Table
              rows={data.requests}
              rowKey={(row) => row.id}
              empty="Nobody has asked for an export or a deletion."
              columns={[
                {
                  key: 'kind',
                  header: 'Kind',
                  strong: true,
                  render: (row) => (
                    <Badge tone={row.kind === 'delete' ? 'alert' : 'cyan'}>
                      {titleCase(row.kind)}
                    </Badge>
                  ),
                },
                {
                  key: 'user',
                  header: 'Account',
                  render: (row) => (
                    <Link className="mono" to={`/users/${row.user_id}`}>
                      {row.user_id.slice(0, 8)}…
                    </Link>
                  ),
                },
                { key: 'status', header: 'Status', render: (row) => <Badge>{titleCase(row.status)}</Badge> },
                { key: 'created', header: 'Received', render: (row) => <span className="dim">{dateTime(row.created_at)}</span> },
                {
                  key: 'sla',
                  header: 'SLA clock',
                  render: (row) =>
                    row.status === 'completed' || row.status === 'rejected' ? (
                      <span className="dim">closed in {row.days_open} d</span>
                    ) : (
                      <Badge tone={slaTone(row.days_open, row.sla_days)}>
                        {row.days_open} / {row.sla_days} days
                      </Badge>
                    ),
                },
                {
                  key: 'artifact',
                  header: 'Certificate',
                  render: (row) =>
                    row.artifact_file_id ? (
                      <span className="mono dim">{row.artifact_file_id.slice(0, 8)}…</span>
                    ) : (
                      <span className="dim">—</span>
                    ),
                },
                {
                  key: 'actions',
                  header: '',
                  render: (row) => (
                    <span className="row" style={{ gap: 6 }}>
                      {(NEXT_STATUS[row.status] ?? []).map((action) => (
                        <button
                          key={action.value}
                          className={action.danger ? 'btn btn--sm btn--danger' : 'btn btn--sm'}
                          onClick={() =>
                            setActing({ id: row.id, status: action.value, label: action.label })
                          }
                        >
                          {action.label}
                        </button>
                      ))}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </Loaded>
      </Card>

      <Card title="Consent acceptance" hint="By purpose, across every version shown to anyone" flush>
        <Loaded resource={queue}>
          {(data) => (
            <Table
              rows={data.consents}
              rowKey={(row) => row.purpose}
              empty="No consent records yet."
              columns={[
                { key: 'purpose', header: 'Purpose', strong: true, render: (row) => titleCase(row.purpose) },
                { key: 'granted', header: 'Granted', render: (row) => <span className="num">{count(row.granted)}</span> },
                { key: 'declined', header: 'Declined', render: (row) => <span className="num">{count(row.declined)}</span> },
                {
                  key: 'rate',
                  header: 'Acceptance',
                  render: (row) => {
                    const total = row.granted + row.declined
                    return total ? percent(row.granted / total) : '—'
                  },
                },
                {
                  key: 'versions',
                  header: 'Versions shown',
                  render: (row) => (
                    <span className="row row--wrap" style={{ gap: 4 }}>
                      {row.versions.map((v) => (
                        <Badge key={v}>{v}</Badge>
                      ))}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </Loaded>
      </Card>

      {acting && (
        <ReasonDialog
          title={acting.label}
          description={
            acting.status === 'rejected'
              ? 'Rejecting a data-rights request needs a lawful basis, and the person is entitled to be told what it is. Write it as you would say it to them.'
              : acting.status === 'completed'
                ? 'Confirm the work actually happened — a completed deletion that did not delete is worse than an open one.'
                : undefined
          }
          confirmLabel={acting.label}
          danger={acting.status === 'rejected'}
          onClose={() => setActing(null)}
          onConfirm={(reason) =>
            run(
              () =>
                post(`/v1/admin/privacy/requests/${acting.id}/status`, {
                  new_status: acting.status,
                  reason,
                }),
              'Request updated.',
              queue.reload,
            )
          }
        />
      )}
    </Page>
  )
}
